import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  Article as ArticleIcon,
  Category as CategoryIcon,
  Comment as CommentIcon,
  ContactPhone as ContactIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import { useNavigate } from 'react-router-dom';

function Dashboard() {
  const [stats, setStats] = useState({
    posts: 0,
    categories: 0,
    comments: 0,
    unreadMessages: 0,
    consultants: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // دریافت آمار کلی
      const [postsRes, categoriesRes, commentsRes, messagesRes, consultantsRes] = await Promise.all([
        fetch(getApiUrl('/api/blog'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(getApiUrl('/api/categories'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(getApiUrl('/api/comments'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(getApiUrl('/api/contact-messages/unread/count'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(getApiUrl('/api/consultants'), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [postsData, categoriesData, commentsData, messagesData, consultantsData] = await Promise.all([
        postsRes.json(),
        categoriesRes.json(),
        commentsRes.json(),
        messagesRes.json(),
        consultantsRes.json()
      ]);

      setStats({
        posts: postsData.length || 0,
        categories: categoriesData.length || 0,
        comments: commentsData.length || 0,
        unreadMessages: messagesData.count || 0,
        consultants: consultantsData.length || 0
      });

      // دریافت آخرین پست‌ها
      const recentPostsData = postsData.slice(0, 5);
      setRecentPosts(recentPostsData);

      // دریافت آخرین پیام‌ها
      const messagesListRes = await fetch(getApiUrl('/api/contact-messages?limit=5'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const messagesListData = await messagesListRes.json();
      setRecentMessages(messagesListData.slice(0, 5));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('خطا در دریافت اطلاعات داشبورد');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-2px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: color }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ fontFamily: 'Vazir' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, fontFamily: 'Vazir' }}>
          داشبورد مدیریت
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Vazir' }}>
          خوش آمدید! اینجا می‌توانید وضعیت کلی سایت را مشاهده کنید.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="مقالات"
            value={stats.posts}
            icon={<ArticleIcon sx={{ fontSize: 40 }} />}
            color="#1976d2"
            onClick={() => navigate('/blog')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="دسته‌بندی‌ها"
            value={stats.categories}
            icon={<CategoryIcon sx={{ fontSize: 40 }} />}
            color="#388e3c"
            onClick={() => navigate('/category')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="نظرات"
            value={stats.comments}
            icon={<CommentIcon sx={{ fontSize: 40 }} />}
            color="#f57c00"
            onClick={() => navigate('/comments')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="پیام‌های جدید"
            value={stats.unreadMessages}
            icon={<ContactIcon sx={{ fontSize: 40 }} />}
            color="#d32f2f"
            onClick={() => navigate('/contact-messages')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="مشاوران"
            value={stats.consultants}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="#7b1fa2"
            onClick={() => navigate('/consultants')}
          />
        </Grid>
      </Grid>

      {/* Recent Content */}
      <Grid container spacing={3}>
        {/* Recent Posts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'Vazir' }}>
                آخرین مقالات
              </Typography>
              <Button 
                size="small" 
                onClick={() => navigate('/blog')}
                sx={{ fontFamily: 'Vazir' }}
              >
                مشاهده همه
              </Button>
            </Box>
            <List>
              {recentPosts.map((post, index) => (
                <React.Fragment key={post.id}>
                  <ListItem>
                    <ListItemIcon>
                      <ArticleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={post.title}
                      secondary={new Date(post.created_at).toLocaleDateString('fa-IR')}
                      sx={{ fontFamily: 'Vazir' }}
                    />
                  </ListItem>
                  {index < recentPosts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {recentPosts.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="هیچ مقاله‌ای یافت نشد"
                    sx={{ fontFamily: 'Vazir', textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Recent Messages */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'Vazir' }}>
                آخرین پیام‌ها
              </Typography>
              <Button 
                size="small" 
                onClick={() => navigate('/contact-messages')}
                sx={{ fontFamily: 'Vazir' }}
              >
                مشاهده همه
              </Button>
            </Box>
            <List>
              {recentMessages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <ListItem>
                    <ListItemIcon>
                      <ContactIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={message.name}
                      secondary={message.message?.substring(0, 50) + '...'}
                      sx={{ fontFamily: 'Vazir' }}
                    />
                  </ListItem>
                  {index < recentMessages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {recentMessages.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="هیچ پیامی یافت نشد"
                    sx={{ fontFamily: 'Vazir', textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
